import { ToolApprovalType } from "@/constants/ai-assistant.ts";
import type {
  SecurityGapToolApprovalType,
  ToolApprovalCall,
  ToolApprovalDoEventActionArguments,
  ToolApprovalSecurityGapArguments,
} from "@/_store/ai-assistant.module";

export type ToolApprovalParsedArguments =
  | {
      toolName: ToolApprovalType.DO_EVENT_ACTION;
      arguments: ToolApprovalDoEventActionArguments;
    }
  | {
      toolName: SecurityGapToolApprovalType;
      arguments: ToolApprovalSecurityGapArguments;
    };

/** Validates that the input is an object before checking expected fields. */
const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null;

/** Checks tool arguments match the event action payload shape. */
const isDoEventActionArguments = (value: unknown): value is ToolApprovalDoEventActionArguments =>
  isRecord(value) && typeof value.eventId === "string";

/** Checks tool arguments match the security gap payload shape. */
const isSecurityGapArguments = (value: unknown): value is ToolApprovalSecurityGapArguments =>
  isRecord(value) && typeof value.securityGapType === "string";

const securityGapToolNames: SecurityGapToolApprovalType[] = [
  ToolApprovalType.UNIGNORE_SECURITY_GAP,
  ToolApprovalType.IGNORE_SECURITY_GAP,
  ToolApprovalType.FIX_SECURITY_GAP,
];

const isSecurityGapToolName = (
  toolName: ToolApprovalType
): toolName is SecurityGapToolApprovalType =>
  securityGapToolNames.includes(toolName as SecurityGapToolApprovalType);

/**
 * Parses tool approval arguments from stringified JSON and narrows by tool type.
 */
export const parseToolApprovalArguments = (
  tool: ToolApprovalCall
): ToolApprovalParsedArguments | null => {
  let parsedArguments: unknown;

  try {
    parsedArguments = JSON.parse(tool.toolArguments);
  } catch {
    return null;
  }

  if (tool.toolName === ToolApprovalType.DO_EVENT_ACTION) {
    return isDoEventActionArguments(parsedArguments)
      ? { toolName: tool.toolName, arguments: parsedArguments }
      : null;
  }

  if (isSecurityGapToolName(tool.toolName) && isSecurityGapArguments(parsedArguments)) {
    return {
      toolName: tool.toolName,
      arguments: parsedArguments,
    };
  }

  return null;
};
